import { memo, useMemo, type JSX } from 'react';
import { useViewabilityTracker } from '@/hooks/useViewabilityTracker';
import { EVENTS, PROPERTIES, trackEvent } from '@/services/posthog';
import type { Product } from '@/types/Product.types';
import styles from './DoubleColumnProductGrid.module.css';
import type {
  ColumnData,
  DoubleColumnProductGridProps,
} from './DoubleColumnProductGrid.types';

interface ProductCardWithTrackingProps {
  product: Product;
  additionalFieldsTracking?: Record<string, string | number | boolean>;
  children: React.ReactNode;
}

const ProductCardWithTracking = ({
  product,
  additionalFieldsTracking,
  children,
}: ProductCardWithTrackingProps): JSX.Element => {
  const viewRef = useViewabilityTracker<HTMLDivElement>({
    onViewable: () => {
      trackEvent(EVENTS.PRODUCT_VIEW, {
        [PROPERTIES.PRODUCT_ID]: product.id,
        [PROPERTIES.PRODUCT_NAME]: product.name,
        [PROPERTIES.PRICE]: parseFloat(
          product.discountPrice || product.price || '0'
        ),
        [PROPERTIES.BRAND]: product.brand || '',
        [PROPERTIES.IS_AVAILABLE]: product.isAvailable ?? false,
        ...additionalFieldsTracking,
      });
    },
    once: true,
    threshold: 0.5,
  });

  return <div ref={viewRef}>{children}</div>;
};

const DoubleColumnProductGrid = ({
  products,
  renderProductItem,
  className = '',
  paddingBetween,
  paddingBottom,
  additionalFieldsTracking,
}: DoubleColumnProductGridProps): JSX.Element => {
  const { colOneData, colTwoData } = useMemo(() => {
    if (!products || !Array.isArray(products))
      return { colOneData: [], colTwoData: [] };

    return products.reduce<ColumnData>(
      (acc, product) => {
        if (acc.colOneData.length <= acc.colTwoData.length) {
          acc.colOneData.push(product);
        } else {
          acc.colTwoData.push(product);
        }
        return acc;
      },
      { colOneData: [], colTwoData: [] }
    );
  }, [products]);

  const renderColumn = (
    data: Product[],
    isFirstColumn: boolean
  ): JSX.Element => (
    <div className={styles.column}>
      {data.map((product, index) => (
        <div
          key={`col${isFirstColumn ? '1' : '2'}-${index}`}
          style={{
            paddingBottom:
              data.length - 1 === index ? paddingBottom : paddingBetween,
          }}
        >
          <ProductCardWithTracking
            product={product}
            additionalFieldsTracking={additionalFieldsTracking}
          >
            {renderProductItem(product)}
          </ProductCardWithTracking>
        </div>
      ))}
    </div>
  );

  return (
    <div className={`${styles.content} ${className}`}>
      {renderColumn(colOneData, true)}
      {renderColumn(colTwoData, false)}
    </div>
  );
};

export default memo(DoubleColumnProductGrid);
